import React from "react"
import * as authModel from "src/models/auth.model"
import useApi from "src/hooks/use-api"
import {
  type To,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router"
import { routeNames } from "src/router/route-names"
import { logger } from "@myvp/shared/src/beacons/beacons.model"
import { createReducer } from "@myvp/shared/src/functions/create-reducer"
import { getLocale } from "src/i18n/get-locale"
import useDidUpdate from "@myvp/shared/src/hooks/use-did-update"
import { Role, GetUserMetadataQuery, UserStudyRole } from "src/gql/graphql"
import { useQueryClient } from "@tanstack/react-query"
import { SUPPORTED_LOCALES } from "src/i18n/locales"
import { SupportedLocale } from "src/types"
import { useI18nState } from "@myvp/shared/src/i18n/i18n-state-provider"
import { type VirtualTimeStudyRoleContext } from "@myvp/todos/src/uat-time-travel/user-virtual-time.model"
import PatientVirtualClock from "src/components/patient-virtual-clock"

export interface Value {
  updateUserInfo: (
    userInfo: Omit<GetUserMetadataQuery["userInfo"], "authId">,
    options?: {
      redirect?: boolean
      searchParams?: object
      customState?: object
    }
  ) => void
  updateUserStudies: (
    userStudies: GetUserMetadataQuery["userStudies"],
    options?: {
      redirect?: boolean
      searchParams?: object
      customState?: object
    }
  ) => Promise<void>
  currentStudyDetails: UserStudyRole["studyDetails"]
  isUserLoggedIn?: boolean
  isUserApiFetching?: boolean
  logout?: (url: To) => void
  participantRelatedUserId?: string
  siteVaultParticipantId?: string
  relatedUserRoleId?: string
  role?: Role
  studyId?: string
  studyLocale?: string
}
type State = Value &
  GetUserMetadataQuery & {
    userInfo: GetUserMetadataQuery["userInfo"] & {
      languageTag: string
      timeZoneId: string
      timeZoneLabel: string
    }
  }
const Context = React.createContext<State>({
  userInfo: {
    languageTag: "",
    timeZoneId: "",
    timeZoneLabel: "",
    authId: "",
  },
  userStudies: {
    userStudyRoles: [],
  },
  getDoesAccountUseVirtualTime: { virtualTimeAllowed: false },
  currentStudyDetails: {},
  updateUserInfo: () => {},
  updateUserStudies: async () => {},
})
const reducer = createReducer({
  UPDATE_METADATA: (state, action) => {
    let updatedState = {
      ...state,
      data: {
        ...state.data,
        ...action.metadata,
      },
    }
    authModel.persistUserMetadata(updatedState.data)
    return updatedState
  },
})

export const UserProvider = (props: {
  children:
    | React.ReactNode
    | ((value: Value & GetUserMetadataQuery) => React.ReactNode)
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const participantRelatedUserId = searchParams.get("relatedUserId")
  const role = searchParams.get("role") as Role
  const studyId = searchParams.get("studyId")

  const { dispatch, ...userApi } = useApi(() => authModel.getUserMetadata(), {
    lazy: false,
    onError: (e) => {
      // oxlint-disable-next-line no-console
      console.error(e)
    },
    reducer,
  })
  const queryClient = useQueryClient()
  const {
    userInfo,
    userStudies,
    getDoesAccountUseVirtualTime = { virtualTimeAllowed: false },
  } = userApi.data ?? {}
  const {
    firstName,
    lastName,
    email,
    username,
    formattedPhoneNumber,
    pendingEmail,
    phoneNumber,
    timeZoneId,
    newTermsVersion,
    authId,
  } = userInfo ?? {}

  const defaultStudy = userStudies?.userStudyRoles?.find((userStudy) => {
    return userStudy.isDefaultStudy
  })
  const currentStudy =
    !studyId || !role || !participantRelatedUserId
      ? null
      : userStudies?.userStudyRoles?.find((userStudy) => {
          return (
            userStudy.studyDetails.studyId === studyId &&
            userStudy.role === role &&
            userStudy.participantDetails.relatedUserId ===
              participantRelatedUserId
          )
        })
  const studyLocale = currentStudy?.languageTag ?? defaultStudy?.languageTag
  const siteVaultParticipantId =
    currentStudy?.participantDetails.siteVaultParticipantId ??
    defaultStudy?.participantDetails.siteVaultParticipantId
  const timeZoneLabel =
    currentStudy?.timeZoneLabel ?? defaultStudy?.timeZoneLabel ?? ""

  // handles:
  // 1. when the page initially loads and the URL params need to be set with the default study
  // 2. the user changes their study in the switch studies modal and the URL params need to be updated
  // 3. Removing the locale from the URL when coming from an email, since this useDidUpdate always runs when userStudies loads, we can rely on this to remove that locale
  useDidUpdate(userStudies, (_, userStudies) => {
    if (userStudies) {
      const defaultStudy = userStudies.userStudyRoles.find(
        (study) => study.isDefaultStudy
      )
      const newSearchParams = new URLSearchParams(searchParams)
      if (defaultStudy && defaultStudy.studyDetails.studyId) {
        newSearchParams.set("role", defaultStudy.role)
        newSearchParams.set("studyId", defaultStudy.studyDetails.studyId)
        newSearchParams.set(
          "relatedUserId",
          defaultStudy.participantDetails.relatedUserId
        )
      }
      if (
        // if we have a valid session and
        // if there is a locale query param
        searchParams.has("locale") &&
        // if this is a deep link from a notification (email)
        searchParams.has("notificationRequestId")
      ) {
        // remove the locale query param so that the study language can be used instead
        newSearchParams.delete("locale")
      }
      if (newSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(newSearchParams, {
          replace: true,
          state: location.state,
        })
      }
    }
  })

  const languageTag = getLocale(
    studyLocale ?? SUPPORTED_LOCALES.enUS
  ) as SupportedLocale
  const { locale: currentLocale, updateIntl } = useI18nState()
  React.useEffect(() => {
    if (languageTag && currentLocale && languageTag !== currentLocale) {
      updateIntl(languageTag)
    }
  }, [currentLocale, languageTag, updateIntl])

  const logout: Value["logout"] = React.useCallback(
    async (url: To) => {
      queryClient.clear()
      logger("ACTIVITY_USER_LOGOUT", { userid: authId })
      await authModel.logout()
      // Reset browser locale when the user is logged out
      updateIntl()
      if (url) {
        navigate(url)
      } else {
        navigate({ pathname: routeNames.login(), search: location.search })
      }
    },
    [authId, navigate, queryClient, location.search, updateIntl]
  )

  React.useEffect(() => {
    const abortController = new AbortController()
    window.addEventListener(
      "storage",
      (event) => {
        if (
          event.storageArea === localStorage &&
          event.key === "myvp-login" &&
          event.oldValue !== event.newValue
        ) {
          // This will get triggered in the authModel.login function
          //
          // When a different tab has logged in, then we want to ensure we remove the
          // user's metadata inside of sessionStorage, if not, then it will display old tab's
          // metadata in the logout popup upon refresh, since we do not fetch user metadata
          // if there is already data saved in sessionStorage
          authModel.removeUserMetadata()
        } else if (
          event.storageArea === localStorage &&
          event.key === "myvp-refresh" &&
          event.oldValue !== event.newValue
        ) {
          // Refresh page if necessary
          authModel.removeUserMetadata()
          window.location.reload()
        }
      },
      { signal: abortController.signal }
    )
    return () => {
      abortController.abort()
    }
  }, [])

  const updateUserInfo = React.useCallback(
    (_userInfo: GetUserMetadataQuery["userInfo"]) => {
      const metadata = { userInfo: { ...userInfo, ..._userInfo }, userStudies }
      dispatch({ type: "UPDATE_METADATA", metadata })
    },
    [dispatch, userStudies, userInfo]
  )
  const updateUserStudies = React.useCallback(
    async (_userStudies: GetUserMetadataQuery["userStudies"]) => {
      const metadata = {
        userInfo,
        userStudies: { ...userStudies, ..._userStudies },
      }
      dispatch({ type: "UPDATE_METADATA", metadata })
    },
    [dispatch, userInfo, userStudies]
  )

  const value = {
    updateUserInfo,
    updateUserStudies,
    userInfo: {
      firstName,
      lastName,
      email,
      username,
      formattedPhoneNumber,
      pendingEmail,
      phoneNumber,
      timeZoneId,
      timeZoneLabel,
      languageTag,
      newTermsVersion,
    },
    isUserLoggedIn: !!userApi.called && !userApi.errors,
    currentStudyDetails:
      currentStudy?.studyDetails ?? defaultStudy?.studyDetails,
    isUserApiFetching: userApi.isFetching || !userApi.called,
    logout,
    participantRelatedUserId,
    relatedUserRoleId:
      currentStudy?.relatedUserRoleId ?? defaultStudy?.relatedUserRoleId,
    siteVaultParticipantId,
    role,
    getDoesAccountUseVirtualTime,
    studyId,
    studyLocale,
    userStudies,
  }

  return (
    <Context.Provider value={value as State}>
      <PatientVirtualClock
        studyId={undefined}
        role={undefined}
        participantRelatedUserId={undefined}
        {...[
          { studyId, role, participantRelatedUserId },
          {
            studyId: defaultStudy?.studyDetails?.studyId,
            role: defaultStudy?.role,
            participantRelatedUserId:
              defaultStudy?.participantDetails?.relatedUserId,
          },
        ].find((item): item is VirtualTimeStudyRoleContext =>
          Boolean(item.role && item.studyId && item.participantRelatedUserId)
        )}
      >
        {typeof props.children === "function"
          ? props.children(value as State)
          : props.children}
      </PatientVirtualClock>
    </Context.Provider>
  )
}

export const useUserSelector = <T,>(selector: (state: State) => T) => {
  return selector(React.useContext(Context))
}
